import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import style from './GamesPage.module.css';

import logoAr from '../../assets/images/logoAr.png';
import logoEn from '../../assets/images/logoEn.png';

import { doc, setDoc, getDoc } from "firebase/firestore";
import { getDocs, collection, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase";

function GamesPage() {
    const navigate = useNavigate();
    const [isDisplayItems, setIsDisplayItems] = useState(false);
    const [type, setType] = useState(null);
    const [idItems, setIdItems] = useState({});
    const [gameId, setGameId] = useState(null);
    const [gamesList, setGamesList] = useState([]);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        async function loadGames() {
            const snapshot = await getDocs(collection(db, "games"));

            const result = [];

            for (const gameDoc of snapshot.docs) {
                const gameData = gameDoc.data();
                
                const items = [];
                const itemsCollectionPath = `games/${gameData.gameId}/items`;

                for (let i = 1; i <= 10; i++) {
                    const itemRef = doc(db, itemsCollectionPath, 'item' + i.toString());
                    const itemSnapshot = await getDoc(itemRef);

                    if (itemSnapshot.exists()) {
                        items.push(itemSnapshot.data());
                    }
                }

                result.push({
                    ...gameData,
                    items
                });
            }

            setGamesList(result);
        }
    
        loadGames();
    }, []);

    // --- SAVE GAME + ITEMS IN FIRESTORE ---
    async function addToFirebase(items, gameId) {
        try {
            const gameRef = doc(db, "games", gameId.toString());
            await setDoc(gameRef, { gameId: gameId });

            const itemsCollectionPath = `games/${gameId}/items`;

            for (const [key, value] of Object.entries(items)) {
                const itemRef = doc(db, itemsCollectionPath, key);
                await setDoc(itemRef, value);
            }

            console.log("Game + items added successfully!");
        } catch (e) {
            console.error("Firestore error:", e);
        }
    }

    // --- LOAD GIFTS FROM FIRESTORE ---
    async function loadGifts() {
        try {
            const q = query(collection(db, "gifts"), orderBy("level", "asc"));
            const snapshot = await getDocs(q);
            
            const giftsByLevel = {};
            snapshot.forEach((doc) => {
                const giftData = doc.data();
                const level = giftData.level || 1;
                if (!giftsByLevel[level]) {
                    giftsByLevel[level] = [];
                }
                giftsByLevel[level].push(giftData.name);
            });
            
            return giftsByLevel;
        } catch (e) {
            console.error("Error loading gifts:", e);
            return {};
        }
    }

    // --- GET RANDOM GIFTS BY LEVEL ---
    function getRandomGifts(giftsByLevel) {
        const selectedGifts = [];
        
        // Collect all available gifts as fallback
        const allGifts = [];
        for (const level in giftsByLevel) {
            allGifts.push(...giftsByLevel[level]);
        }
        
        // Level 1: take 1
        if (giftsByLevel[1] && giftsByLevel[1].length > 0) {
            const randomIndex = Math.floor(Math.random() * giftsByLevel[1].length);
            selectedGifts.push(giftsByLevel[1][randomIndex]);
        } else if (allGifts.length > 0) {
            // Fallback: use any available gift
            const randomIndex = Math.floor(Math.random() * allGifts.length);
            selectedGifts.push(allGifts[randomIndex]);
        }
        
        // Level 2: take 4 (allow duplicates)
        if (giftsByLevel[2] && giftsByLevel[2].length > 0) {
            for (let i = 0; i < 4; i++) {
                const randomIndex = Math.floor(Math.random() * giftsByLevel[2].length);
                selectedGifts.push(giftsByLevel[2][randomIndex]);
            }
        }
        
        // If we still don't have 5 gifts, fill with random gifts from all levels
        while (selectedGifts.length < 5 && allGifts.length > 0) {
            const randomIndex = Math.floor(Math.random() * allGifts.length);
            selectedGifts.push(allGifts[randomIndex]);
        }
        
        // Shuffle the selected gifts
        for (let i = selectedGifts.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [selectedGifts[i], selectedGifts[j]] = [selectedGifts[j], selectedGifts[i]];
        }
        
        return selectedGifts;
    }

    // --- CREATE GAME ---
    async function CreateGame() {
        const newGameId = Math.floor(Math.random() * 90000) + 10000;

        const generateRandomId = () => {
            const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
            let result = "";
            for (let i = 0; i < 7; i++) {
                result += chars.charAt(Math.floor(Math.random() * chars.length));
            }
            return result;
        };

        // Load gifts and get random selection
        const giftsByLevel = await loadGifts();
        const randomGifts = getRandomGifts(giftsByLevel);

        const winningIndexes = [];
        while (winningIndexes.length < 5) {
            const rand = Math.floor(Math.random() * 10);
            if (!winningIndexes.includes(rand)) {
                winningIndexes.push(rand);
            }
        }

        // Ensure we have exactly 5 gifts for 5 winning items
        if (randomGifts.length < 5) {
            console.error("Not enough gifts available. Need 5 gifts but only have", randomGifts.length);
            alert("Error: Not enough gifts in database. Please add gifts before creating a game.");
            return;
        }

        const newIdItems = {};
        let giftIndex = 0;
        
        // First, assign gifts to all winning items
        for (let i = 0; i < 10; i++) {
            const isWinning = winningIndexes.includes(i);
            
            // Every winning item MUST get a gift
            let gift = "";
            if (isWinning) {
                if (giftIndex < randomGifts.length) {
                    gift = randomGifts[giftIndex++];
                } else {
                    console.error(`CRITICAL: No gift available for winning item ${i + 1}`);
                    // Fallback: use last gift or a default
                    gift = randomGifts[randomGifts.length - 1] || "No gift available";
                }
            }
            
            newIdItems[`item${i + 1}`] = {
                itemId: generateRandomId(),
                isWinning: isWinning,
                gift: gift,
                isUrlCopied: false,
            };
        }
        
        // Verify all winning items have gifts
        const winningItemsWithoutGifts = Object.values(newIdItems).filter(
            item => item.isWinning && (!item.gift || item.gift.trim() === "")
        );
        
        if (winningItemsWithoutGifts.length > 0) {
            console.error("ERROR: Some winning items don't have gifts!", winningItemsWithoutGifts);
            alert("Error: Some winning items don't have gifts. Please check the console.");
        }

        setGameId(newGameId);
        setIdItems(newIdItems);

        addToFirebase(newIdItems, newGameId);
        setIsDisplayItems(true);
        setType("create")
    }

    // --- COPY URL ---
    const copyToClipboard = async (url, itemKey) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(itemKey);
            setTimeout(() => setCopiedId(null), 2000);
            console.log(url)
            console.log(itemKey)
            const itemRef = doc(db, `games/${gameId}/items/${itemKey}`);
            await setDoc(itemRef, { isUrlCopied: true }, { merge: true });

        } catch (err) {
            console.error("Copy failed:", err);
        }
    };

    const IsReady = (g) => {
        const game = gamesList.filter(doc => doc.gameId === g.gameId );

        let isUrlCopiedCount = 0;

        for(const [key, value] of Object.entries(game['0'].items)){
            if(value.isUrlCopied)
                isUrlCopiedCount++;
        }
        
        return <p>Ready? {isUrlCopiedCount} / 10</p>
    }

    const resetDisplayItems = () => {
        setIsDisplayItems(false)
        setType(null)
        setGameId('')
        setIdItems('')
    }

    const DisplayItems = () => {
        return (
            <div className={`${style.createGameContainer} ${isDisplayItems ? style.fadeIn : style.fadeOut}`}>
                <div className={style.closeBtn} onClick={() => resetDisplayItems()}>X</div>

                <p className={style.title}>Create Game</p>

                {gameId && <p className={style.gameId}>Game ID: {gameId}</p>}
                {console.log(type)}
                <button 
                    className={style.repickButton}
                    style={{display: type === "open" ? "block" : "none"}}
                    onClick={repickGifts}
                >
                    Repick Gifts
                </button>

                {/* Item List */}
                <div className={style.idItemsList}>
                    {Object.entries(idItems).map(([key, value]) => {
                        const url = `https://hiddengift.mounersh.com/${gameId}/${value.itemId}`;
                        return (
                            <div key={key} className={style.idItem}>
                                <span className={style.idItemKey}>{type === "open" ? <>Item{key}:</> : <>{key}:</>}</span>

                                <span
                                    className={`${style.idItemValue} ${copiedId === key ? style.copied : ""}`}
                                    onClick={() => copyToClipboard(url, type === "open" ? `Item${key}` : `${key}`, value)}
                                    title="Click to copy"
                                >
                                    {copiedId === key ? "Copied!" : url}
                                </span>

                                {value.isWinning && (
                                    <span className={style.winnerTag}>WIN</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        )
    }

    // --- FIX MISSING GIFTS FOR WINNING ITEMS ---
    async function fixMissingGifts(gameId, items) {
        const giftsByLevel = await loadGifts();
        const randomGifts = getRandomGifts(giftsByLevel);
        
        if (randomGifts.length < 5) {
            console.warn("Not enough gifts to fix missing gifts");
            return items;
        }
        
        let giftIndex = 0;
        const fixedItems = { ...items };
        let needsUpdate = false;
        
        // Find all winning items that don't have gifts
        const winningItemsWithoutGifts = [];
        for (const [key, value] of Object.entries(items)) {
            if (value.isWinning && (!value.gift || value.gift.trim() === "")) {
                winningItemsWithoutGifts.push(key);
            }
        }
        
        // Assign gifts to items missing them
        for (const key of winningItemsWithoutGifts) {
            if (giftIndex < randomGifts.length) {
                fixedItems[key] = {
                    ...fixedItems[key],
                    gift: randomGifts[giftIndex++]
                };
                needsUpdate = true;
            }
        }
        
        // Update in Firestore if needed
        if (needsUpdate) {
            const itemsCollectionPath = `games/${gameId}/items`;
            for (const [key, value] of Object.entries(fixedItems)) {
                if (winningItemsWithoutGifts.includes(key)) {
                    const itemRef = doc(db, itemsCollectionPath, key);
                    await setDoc(itemRef, { gift: value.gift }, { merge: true });
                }
            }
            console.log(`Fixed ${winningItemsWithoutGifts.length} missing gifts for game ${gameId}`);
        }
        
        return fixedItems;
    }

    // --- REPICK GIFTS AND WINNING ITEMS FOR EXISTING GAME ---
    async function repickGifts() {
        if (!gameId || !idItems) {
            console.error("Cannot repick gifts: gameId or idItems is missing");
            return;
        }

        try {
            // Load new random gifts
            const giftsByLevel = await loadGifts();
            const randomGifts = getRandomGifts(giftsByLevel);
            
            if (randomGifts.length < 5) {
                alert("Error: Not enough gifts in database. Please add gifts before repicking.");
                return;
            }

            // Get all item keys (item1, item2, ..., item10)
            const allItemKeys = Object.keys(idItems).sort((a, b) => {
                const numA = parseInt(a.replace('item', ''));
                const numB = parseInt(b.replace('item', ''));
                return numA - numB;
            });

            // Pick 5 new random winning items
            const winningIndexes = [];
            while (winningIndexes.length < 5) {
                const rand = Math.floor(Math.random() * allItemKeys.length);
                if (!winningIndexes.includes(rand)) {
                    winningIndexes.push(rand);
                }
            }

            // Create updated items object
            const updatedItems = {};
            let giftIndex = 0;
            const itemsCollectionPath = `games/${gameId}/items`;

            // Update all items: set isWinning status and assign gifts to new winning items
            for (let i = 0; i < allItemKeys.length; i++) {
                const key = allItemKeys[i];
                const isWinning = winningIndexes.includes(i);
                const currentItem = idItems[key];

                // Preserve itemId and isUrlCopied, update isWinning and gift
                updatedItems[key] = {
                    itemId: currentItem.itemId,
                    isWinning: isWinning,
                    gift: isWinning ? randomGifts[giftIndex++] : "",
                    isUrlCopied: currentItem.isUrlCopied,
                };

                // Update in Firestore
                const itemRef = doc(db, itemsCollectionPath, key);
                await setDoc(itemRef, {
                    isWinning: isWinning,
                    gift: updatedItems[key].gift
                }, { merge: true });
            }

            // Update local state
            setIdItems(updatedItems);
            console.log(`Repicked winning items and gifts for game ${gameId}`);
            alert("Winning items and gifts have been repicked successfully!");
        } catch (error) {
            console.error("Error repicking gifts:", error);
            alert("Error repicking gifts. Please try again.");
        }
    }

    const openGame = async (g) => {
        setGameId(g);

        const game = gamesList.filter(doc => doc.gameId === g );
        console.log(game['0'].items)

        let items = {}

        for (const [key, value] of Object.entries(game['0'].items)) {
            const itemId = 'item'+(parseInt(key, 10) + 1);
            items[itemId] = {
                itemId: value.itemId,
                isWinning: value.isWinning,
                gift: value.gift,
                isUrlCopied: value.isUrlCopied,
            };
        }
        
        // Fix any missing gifts for winning items
        items = await fixMissingGifts(g, items);
        
        setIdItems(items)
        DisplayItems({type: "open"});
        setIsDisplayItems(true)
        setType("open")
    }

    return (
        <div className={style.page}>
            <div className={style.imgbg}/>
            <div className={style.bg}/>

            {/* Main Screen */}
            <div className={`${style.container} ${isDisplayItems ? style.fadeOut : style.fadeIn}`}>
                <div className={style.logos}>
                    <img className={style.logoEn} src={logoEn} alt="Logo EN" />
                    <img className={style.logoAr} src={logoAr} alt="Logo AR" />
                </div>

                <p className={style.title}>Games</p>

                <div className={style.gamesList}>
                    {gamesList.length === 0 && (
                        <p className={style.noGames}>No games created yet.</p>
                    )}

                    {gamesList.map((g, index) => (
                        <div
                            key={index}
                            className={style.gameItem}
                            onClick={() => openGame(g.gameId)}
                        >
                            <span className={style.gameIdText}>Game ID: {g.gameId}</span>
                            <IsReady gameId={g.gameId}/>
                        </div>
                    ))}
                </div>

                <button onClick={CreateGame}>Create Game</button>
            </div>

            {isDisplayItems && (
                <DisplayItems />
            )}
        </div>
    );
}

export default GamesPage;
